import React, { useState, useMemo } from 'react';
import { simularCredito, simularCuotaFija, NOMBRE_CUOTA } from '../utils/simulador';

// El simulador, para contestar con el cliente delante.
//
// «Préstame 500.000 al 10% y te los pago en dos meses, ¿cuánto te doy
// semanal?». Antes esa cuenta se hacía de cabeza, y de cabeza sale mal en
// cuanto el cliente abona a capital: el interés del segundo mes ya no es el
// mismo. Aquí sale del mismo motor que lleva los préstamos vivos, así que lo
// que se promete en la calle es lo que el programa va a cobrar después.

const moneda = (n) => '$' + Math.round(n || 0).toLocaleString('es-CO');

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun',
               'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const fecha = (d) => {
  const f = d instanceof Date ? d : new Date(d);
  if (isNaN(f.getTime())) return '';
  return `${f.getUTCDate()} ${MESES[f.getUTCMonth()]} ${f.getUTCFullYear()}`;
};

const CAJA = {
  background: 'white', borderRadius: '10px', padding: '18px',
  boxShadow: '0 1px 3px rgba(0,0,0,.08)', marginBottom: '16px',
};
const CAMPO = {
  width: '100%', padding: '10px 12px', borderRadius: '7px',
  border: '1px solid #d1d5db', fontSize: '15px',
};
const BOTON = {
  padding: '9px 16px', borderRadius: '7px', border: '1px solid #d1d5db',
  background: 'white', cursor: 'pointer', fontSize: '14px',
};
const ROTULO = {
  display: 'block', fontSize: '12px', color: '#6b7280',
  marginBottom: '5px', fontWeight: 600,
};

function Campo({ etiqueta, children, ancho }) {
  return (
    <div style={{ flex: ancho || '1 1 10rem', minWidth: '9rem' }}>
      <label style={ROTULO}>{etiqueta}</label>
      {children}
    </div>
  );
}

// Para decir «8% × 8 meses» y no «8 × 8 monthly».
const NOMBRE_PERIODO = {
  monthly: 'meses', biweekly: 'quincenas', weekly: 'semanas', daily: 'días',
};

function Simulador() {
  const [monto, setMonto] = useState('500000');
  const [tasa, setTasa] = useState('10');
  const [frecuenciaInteres, setFrecuenciaInteres] = useState('monthly');
  const [frecuenciaPago, setFrecuenciaPago] = useState('weekly');
  const [plazo, setPlazo] = useState('2');
  const [unidadPlazo, setUnidadPlazo] = useState('meses');
  const [cuotaFija, setCuotaFija] = useState(false);
  const [verDetalle, setVerDetalle] = useState(false);
  const [verCalendario, setVerCalendario] = useState(false);

  // Se recalcula al teclear: la gracia del simulador es que la respuesta esté
  // antes de que el cliente termine la frase.
  const s = useMemo(() => {
    try {
      // Son dos negocios distintos, no dos ajustes del mismo. En cuota fija el
      // interés se pacta una vez y no se mueve; en el otro se recalcula sobre
      // lo que va quedando. Por eso cambia hasta lo que se pregunta: allí un
      // plazo, aquí un número de cuotas.
      if (cuotaFija) {
        // Las mismas casillas que el otro modo: el interés con su «cada», y el
        // plazo. Cuántas cuotas son sale de ahí — quincenal a 8 meses son 16 —
        // y no se teclea. Preguntar las dos cosas deja escribir un plazo y un
        // número de cuotas que no se corresponden.
        return simularCuotaFija({
          monto: parseFloat(monto), tasa: parseFloat(tasa),
          frecuenciaInteres, frecuenciaPago,
          plazo: parseInt(plazo, 10), unidadPlazo,
        });
      }
      return simularCredito({
        monto: parseFloat(monto), tasa: parseFloat(tasa),
        frecuenciaInteres, frecuenciaPago,
        plazo: parseInt(plazo, 10), unidadPlazo,
      });
    } catch { return null; }
  }, [cuotaFija, monto, tasa, frecuenciaInteres, frecuenciaPago,
      plazo, unidadPlazo]);

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>📐 Simular un préstamo</h2>
      <p style={{ color: '#6b7280', marginTop: '-8px' }}>
        Para contestar con el cliente delante, sin crear nada
      </p>

      <div style={CAJA}>
        {/* El interruptor va arriba del todo porque cambia las preguntas de
            abajo: no es un detalle del final, es de qué negocio se habla. */}
        <label style={{
          display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer',
          marginBottom: '16px', paddingBottom: '14px',
          borderBottom: '1px solid #e5e7eb',
        }}>
          <input type="checkbox" checked={cuotaFija} style={{ width: 'auto' }}
                 onChange={e => setCuotaFija(e.target.checked)} />
          {/* Decía «el interés se pacta una vez», y eso se leía como que el
              porcentaje se aplica una sola vez — que es justo el enredo que
              hizo falta desenredar. Lo que se pacta una vez es el TOTAL. */}
          <span>💵 <strong>Cuota fija</strong> — el total se pacta el primer día
            y la cuota no cambia</span>
        </label>

        <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
          <Campo etiqueta="Cuánto pide">
            <input type="number" value={monto} onChange={e => setMonto(e.target.value)}
                   style={CAMPO} inputMode="numeric" />
          </Campo>
          <Campo etiqueta="Interés (%)" ancho="0 1 7rem">
            <input type="number" value={tasa} onChange={e => setTasa(e.target.value)}
                   style={CAMPO} inputMode="decimal" step="0.1" />
          </Campo>
          {/* También en cuota fija. Decía que ahí el interés «no se cobra
              cada nada», y era mentira: cuando alguien pacta el 8% a ocho
              meses, son ocho veces el 8%. Escondiendo esta casilla el 8 se
              leía como el interés del préstamo entero y el préstamo salía a
              la octava parte de lo pactado. */}
          <Campo etiqueta="El interés se cobra">
            <select value={frecuenciaInteres} style={CAMPO}
                    onChange={e => setFrecuenciaInteres(e.target.value)}>
              <option value="monthly">cada mes</option>
              <option value="biweekly">cada quincena</option>
              <option value="weekly">cada semana</option>
              <option value="daily">cada día</option>
            </select>
          </Campo>
        </div>

        <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginTop: '14px' }}>
          <Campo etiqueta="En cuánto tiempo paga" ancho="0 1 7rem">
            <input type="number" value={plazo} onChange={e => setPlazo(e.target.value)}
                   style={CAMPO} inputMode="numeric" min="1" />
          </Campo>
          <Campo etiqueta=" " ancho="0 1 9rem">
            <select value={unidadPlazo} style={CAMPO}
                    onChange={e => setUnidadPlazo(e.target.value)}>
              <option value="meses">meses</option>
              <option value="semanas">semanas</option>
              <option value="dias">días</option>
            </select>
          </Campo>
          <Campo etiqueta="El cliente me paga">
            <select value={frecuenciaPago} style={CAMPO}
                    onChange={e => setFrecuenciaPago(e.target.value)}>
              <option value="daily">todos los días</option>
              <option value="weekly">cada semana</option>
              <option value="biweekly">cada quincena</option>
              <option value="monthly">cada mes</option>
            </select>
          </Campo>
        </div>
      </div>

      {!s ? (
        <div style={{ ...CAJA, textAlign: 'center', color: '#6b7280' }}>
          Escriba cuánto pide y a qué interés.
        </div>
      ) : (
        <>
          <div style={{
            background: 'white', border: '2px solid #667eea', borderRadius: '12px',
            padding: '22px', marginBottom: '16px',
            boxShadow: '0 4px 12px rgba(102,126,234,.15)',
          }}>
            <div style={{ color: '#6b7280', fontSize: '14px' }}>
              Tiene que darle {NOMBRE_CUOTA[frecuenciaPago]}
            </div>
            <div style={{ fontSize: '40px', fontWeight: 'bold', color: '#4338ca', lineHeight: 1.15 }}>
              {moneda(s.cuota)}
            </div>
            <div style={{ fontSize: '14px', color: '#4b5563', marginTop: '4px' }}>
              <strong>{s.cuotas}</strong> {s.cuotas === 1 ? 'cuota' : 'cuotas'}
              {' · '}la última el <strong>{fecha(s.vencimiento)}</strong>
            </div>

            <div style={{
              display: 'flex', gap: '26px', marginTop: '16px', paddingTop: '14px',
              borderTop: '1px solid #e5e7eb', flexWrap: 'wrap',
            }}>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>Le entrega</div>
                <div style={{ fontWeight: 700, color: '#991b1b' }}>{moneda(s.capital)}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>Le devuelve en total</div>
                <div style={{ fontWeight: 700 }}>{moneda(s.totalAPagar)}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>Usted gana</div>
                <div style={{ fontWeight: 700, color: '#15803d' }}>{moneda(s.ganancia)}</div>
              </div>
            </div>

            {/* DE DÓNDE SALE EL INTERÉS, y qué escribir al crearlo.
                
                La cuenta se enseña porque es la que el cliente va a querer
                revisar delante de uno: 8% por 8 meses son 64%.
                
                Y el aviso del final hace falta mientras la pantalla de crear
                siga pidiendo el porcentaje del préstamo entero. Sin él, quien
                simula al 8% escribe 8 allá y le arma al cliente un préstamo por
                la octava parte de lo pactado. */}
            {s.modalidad === 'cuota_fija' && s.tasaTotal !== s.tasa && (
              <div style={{
                marginTop: '14px', padding: '10px 12px', borderRadius: '8px',
                background: '#fffbeb', border: '1px solid #fde68a',
                fontSize: '13px', color: '#78350f',
              }}>
                {s.tasa}% × {s.periodos % 1 === 0 ? s.periodos : s.periodos.toFixed(2)}{' '}
                {NOMBRE_PERIODO[s.frecuenciaInteres] || 'períodos'}
                {' = '}<strong>{s.tasaTotal}% del préstamo</strong>, o sea{' '}
                {moneda(s.ganancia)}.
                <div style={{ marginTop: '6px' }}>
                  Al <strong>crear</strong> el préstamo, en «interés» va{' '}
                  <strong>{s.tasaTotal}</strong> — esa pantalla todavía pide el
                  porcentaje del préstamo completo, no el de cada mes.
                </div>
              </div>
            )}

            {s.cuota !== s.cuotaExacta && (
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '12px' }}>
                La cuenta exacta da {moneda(s.cuotaExacta)}. Se redondea hacia
                arriba para que sea una cifra de cobrar
                {/* En cuota fija el total se pactó el primer día y no puede
                    crecer por un redondeo: se ajusta la última. En el otro
                    modelo el sobrante queda a favor del cliente. */}
                {s.modalidad === 'cuota_fija'
                  ? `, y la última cuota queda en ${moneda(s.ultimaCuota)} para que la suma dé exacta.`
                  : (s.sobraAlFinal > 0
                      ? `; al final le sobran ${moneda(s.sobraAlFinal)} a favor del cliente.`
                      : '.')}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
            <button onClick={() => setVerCalendario(v => !v)}
                    style={BOTON}>
              {verCalendario
                ? 'Ocultar las fechas'
                : `Ver las ${s.cuotas} ${s.cuotas === 1 ? 'cuota' : 'cuotas'} una por una`}
            </button>
            {/* En cuota fija no hay cortes mensuales que enseñar: el interés
                no se liquida por meses. */}
            {s.modalidad !== 'cuota_fija' && (
              <button onClick={() => setVerDetalle(v => !v)} style={BOTON}>
                {verDetalle ? 'Ocultar el detalle' : 'Ver mes por mes'}
              </button>
            )}
          </div>

          {/* Las cuotas con su fecha, agrupadas por el corte en que caen. Es lo
              que hay que poder leerle al cliente: «me da 37.000 el 18 de
              agosto, el 25…». La tabla de abajo enseña los cortes, y ahí las
              cuotas salen sumadas —185.000, 148.000—, que no es lo que él
              entrega cada vez. */}
          {verCalendario && s.modalidad === 'cuota_fija' && (
            <div style={{ ...CAJA, maxHeight: '26rem', overflowY: 'auto' }}>
              {s.reparto.map((c) => (
                <div key={c.numero} style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'baseline', gap: '12px', padding: '8px 0',
                  borderBottom: '1px solid #f3f4f6',
                }}>
                  <div>
                    <strong>Cuota {c.numero}</strong>
                    <span style={{ color: '#6b7280', fontSize: '13px' }}>
                      {' · '}{fecha(c.fecha)}
                    </span>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 700 }}>{moneda(c.monto)}</div>
                    <div style={{ fontSize: '11px', color: '#6b7280' }}>
                      {moneda(c.aCapital)} suyo · {moneda(c.aInteres)} de ganancia
                      {' · '}queda {moneda(c.saldo)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {verCalendario && s.modalidad !== 'cuota_fija' && (
            <div style={{ ...CAJA, maxHeight: '26rem', overflowY: 'auto' }}>
              {s.reparto.map((mes, i) => (
                <div key={i} style={{ marginBottom: '18px' }}>
                  <div style={{
                    fontSize: '12px', color: '#6b7280', fontWeight: 600,
                    borderBottom: '1px solid #e5e7eb', paddingBottom: '5px',
                    marginBottom: '7px',
                  }}>
                    MES QUE CIERRA EL {fecha(mes.corte).toUpperCase()}
                    {' · '}interés {moneda(mes.interesDelMes)}
                    {mes.arrastreAnterior > 0 && ` + ${moneda(mes.arrastreAnterior)} de antes`}
                  </div>
                  <table style={{ width: '100%', maxWidth: '34rem',
                                  borderCollapse: 'collapse', fontSize: '14px' }}>
                    <thead>
                      <tr style={{ fontSize: '11px', color: '#9ca3af' }}>
                        <th style={{ textAlign: 'left', fontWeight: 400 }}>Fecha</th>
                        <th style={{ textAlign: 'right', fontWeight: 400 }}>Cuota</th>
                        <th style={{ textAlign: 'right', fontWeight: 400 }}>Al interés</th>
                        <th style={{ textAlign: 'right', fontWeight: 400 }}>A la deuda</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mes.cuotas.map((c, j) => (
                        <tr key={j}>
                          <td style={{ padding: '3px 0', color: '#4b5563' }}>{fecha(c.fecha)}</td>
                          <td style={{ textAlign: 'right', fontWeight: 700 }}>{moneda(c.monto)}</td>
                          <td style={{ textAlign: 'right', color: c.aInteres ? '#b45309' : '#d1d5db' }}>
                            {moneda(c.aInteres)}
                          </td>
                          <td style={{ textAlign: 'right', color: c.aCapital ? '#15803d' : '#d1d5db' }}>
                            {moneda(c.aCapital)}
                          </td>
                        </tr>
                      ))}
                      <tr style={{ borderTop: '1px solid #e5e7eb', fontWeight: 700 }}>
                        <td style={{ padding: '5px 0', color: '#6b7280', fontSize: '12px' }}>
                          Total del mes
                        </td>
                        <td style={{ textAlign: 'right' }}>{moneda(mes.acumulado)}</td>
                        <td style={{ textAlign: 'right', color: '#b45309' }}>{moneda(mes.aInteres)}</td>
                        <td style={{ textAlign: 'right', color: '#15803d' }}>{moneda(mes.aCapital)}</td>
                      </tr>
                      <tr>
                        <td colSpan={3} style={{ fontSize: '12px', color: '#6b7280' }}>
                          Saldo tras este corte
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 700 }}>
                          {moneda(mes.saldoFinal)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ))}
              <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
                Todas las cuotas son de <strong>{moneda(s.cuota)}</strong>. Lo que
                entra en el mes cubre primero el interés y de ahí en adelante baja
                la deuda; por eso las primeras del mes van al interés y las
                siguientes no. Las fechas se cuentan desde hoy: al crear el
                préstamo se ajustan al día en que le entregue la plata.
              </p>
            </div>
          )}

          {verDetalle && (
            <div style={{ ...CAJA, overflowX: 'auto' }}>
              <table className="payments-table" style={{ minWidth: '32rem' }}>
                <thead>
                  <tr>
                    <th>Corte</th>
                    <th>Debía</th>
                    <th>Interés</th>
                    <th>Le entrega</th>
                    <th>Al interés</th>
                    <th>A la deuda</th>
                    <th>Queda</th>
                  </tr>
                </thead>
                <tbody>
                  {s.liquidaciones.map((l, i) => (
                    <tr key={i}>
                      <td style={{ whiteSpace: 'nowrap' }}>{fecha(l.corte)}</td>
                      <td>{moneda(l.saldoInicial)}</td>
                      <td style={{ color: '#b45309' }}>{moneda(l.interesDelMes)}</td>
                      <td>{moneda(l.acumulado)}</td>
                      <td style={{ color: '#b45309' }}>{moneda(l.aInteres)}</td>
                      <td style={{ color: '#15803d' }}>{moneda(l.aCapital)}</td>
                      <td><strong>{moneda(l.saldoFinal)}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: 0 }}>
                Lo que entrega se acumula y se reparte en cada corte: primero el
                interés, y lo que sobra baja la deuda. Por eso el interés del
                segundo mes es menor que el del primero.
              </p>
            </div>
          )}

          <p style={{ fontSize: '13px', color: '#6b7280' }}>
            Esto no crea nada: es solo la cuenta. Para prestarle de verdad, vaya
            a <strong>➕ Prestar</strong>.
          </p>
        </>
      )}
    </div>
  );
}

export default Simulador;
